/**
 * 包括的なエラーハンドリングシステム
 */

/**
 * アプリケーションエラー
 */
export interface AppError {
    /** エラーコード */
    code: string
    /** エラーメッセージ */
    message: string
    /** 詳細情報 */
    details?: any
    /** タイムスタンプ */
    timestamp: string
    /** コンテキスト */
    context?: string
}

/**
 * エラーコンテキスト
 */
export interface ErrorContext {
    /** 操作名 */
    operation: string
    /** ユーザーID */
    userId?: string
    /** テナントID */
    tenantId?: string
    /** リソースID */
    resourceId?: string
    /** 追加データ */
    additionalData?: Record<string, any>
}

/**
 * エラータイプの定義
 */
export enum ErrorType {
    // ネットワークエラー
    NETWORK_ERROR = 'NETWORK_ERROR',
    TIMEOUT_ERROR = 'TIMEOUT_ERROR',
    CONNECTION_ERROR = 'CONNECTION_ERROR',

    // 認証・認可エラー
    AUTHENTICATION_ERROR = 'AUTHENTICATION_ERROR',
    AUTHORIZATION_ERROR = 'AUTHORIZATION_ERROR',
    SESSION_EXPIRED = 'SESSION_EXPIRED',

    // バリデーションエラー
    VALIDATION_ERROR = 'VALIDATION_ERROR',
    INVALID_INPUT = 'INVALID_INPUT',

    // データベースエラー
    DATABASE_ERROR = 'DATABASE_ERROR',
    CONSTRAINT_ERROR = 'CONSTRAINT_ERROR',
    NOT_FOUND = 'NOT_FOUND',
    DUPLICATE_ERROR = 'DUPLICATE_ERROR',

    // ビジネスロジックエラー
    BUSINESS_LOGIC_ERROR = 'BUSINESS_LOGIC_ERROR',
    INSUFFICIENT_PERMISSIONS = 'INSUFFICIENT_PERMISSIONS',
    RESOURCE_CONFLICT = 'RESOURCE_CONFLICT',

    // システムエラー
    INTERNAL_ERROR = 'INTERNAL_ERROR',
    EXTERNAL_SERVICE_ERROR = 'EXTERNAL_SERVICE_ERROR',
    CONFIGURATION_ERROR = 'CONFIGURATION_ERROR',

    // ユーザーエラー
    USER_ERROR = 'USER_ERROR',
    INVALID_OPERATION = 'INVALID_OPERATION',
}

/**
 * エラーレベルの定義
 */
export enum ErrorLevel {
    LOW = 'LOW',
    MEDIUM = 'MEDIUM',
    HIGH = 'HIGH',
    CRITICAL = 'CRITICAL',
}

/**
 * エラーハンドラークラス
 */
export class ErrorHandler {
    private static instance: ErrorHandler;
    private errorLog: AppError[] = [];
    private maxLogSize = 1000;

    private constructor() { }

    public static getInstance(): ErrorHandler {
        if (!ErrorHandler.instance) {
            ErrorHandler.instance = new ErrorHandler();
        }
        return ErrorHandler.instance;
    }

    /**
     * エラーを処理し、適切な形式に変換
     */
    public handleError(
        error: any,
        context: ErrorContext,
        level: ErrorLevel = ErrorLevel.MEDIUM
    ): AppError {
        const appError = this.createAppError(error, context, level);
        this.logError(appError);
        return appError;
    }

    /**
     * エラーをAppError形式に変換
     */
    private createAppError(
        error: any,
        context: ErrorContext,
        level: ErrorLevel
    ): AppError {
        const timestamp = new Date().toISOString();

        // Supabaseエラーの処理
        if (error?.code) {
            return this.handleSupabaseError(error, context, timestamp);
        }

        // ネットワークエラーの処理
        if (error?.name === 'NetworkError' || error?.message?.includes('fetch')) {
            return {
                code: ErrorType.NETWORK_ERROR,
                message: 'ネットワークエラーが発生しました。インターネット接続を確認してください。',
                details: error,
                timestamp,
                context: context.operation,
            };
        }

        // タイムアウトエラーの処理
        if (error?.name === 'TimeoutError' || error?.message?.includes('timeout')) {
            return {
                code: ErrorType.TIMEOUT_ERROR,
                message: 'リクエストがタイムアウトしました。しばらく時間をおいて再試行してください。',
                details: error,
                timestamp,
                context: context.operation,
            };
        }

        // 一般的なエラーの処理
        return {
            code: ErrorType.INTERNAL_ERROR,
            message: error?.message || '予期しないエラーが発生しました。',
            details: error,
            timestamp,
            context: context.operation,
        };
    }

    /**
     * Supabaseエラーの処理
     */
    private handleSupabaseError(
        error: any,
        context: ErrorContext,
        timestamp: string
    ): AppError {
        const { code, message, details } = error;

        switch (code) {
            case 'PGRST116':
                return {
                    code: ErrorType.NOT_FOUND,
                    message: '指定されたリソースが見つかりません。',
                    details: error,
                    timestamp,
                    context: context.operation,
                };

            case 'PGRST204':
                return {
                    code: ErrorType.DATABASE_ERROR,
                    message: 'データベーススキーマエラーが発生しました。管理者にお問い合わせください。',
                    details: error,
                    timestamp,
                    context: context.operation,
                };

            case '22P02':
                return {
                    code: ErrorType.VALIDATION_ERROR,
                    message: 'データ形式が正しくありません。',
                    details: error,
                    timestamp,
                    context: context.operation,
                };

            case '23505':
                return {
                    code: ErrorType.DUPLICATE_ERROR,
                    message: '重複するデータが存在します。',
                    details: error,
                    timestamp,
                    context: context.operation,
                };

            case '23503':
                return {
                    code: ErrorType.CONSTRAINT_ERROR,
                    message: '関連するデータが存在しないため、操作を実行できません。',
                    details: error,
                    timestamp,
                    context: context.operation,
                };

            case '42501':
                return {
                    code: ErrorType.AUTHORIZATION_ERROR,
                    message: 'この操作を実行する権限がありません。',
                    details: error,
                    timestamp,
                    context: context.operation,
                };

            case 'PGRST301':
                return {
                    code: ErrorType.AUTHENTICATION_ERROR,
                    message: '認証が必要です。ログインしてください。',
                    details: error,
                    timestamp,
                    context: context.operation,
                };

            default:
                return {
                    code: ErrorType.DATABASE_ERROR,
                    message: message || 'データベースエラーが発生しました。',
                    details: error,
                    timestamp,
                    context: context.operation,
                };
        }
    }

    /**
     * エラーをログに記録
     */
    private logError(error: AppError): void {
        this.errorLog.push(error);

        // ログサイズの制限
        if (this.errorLog.length > this.maxLogSize) {
            this.errorLog = this.errorLog.slice(-this.maxLogSize);
        }

        // コンソールにログ出力（開発環境のみ）
        if (process.env.NODE_ENV === 'development') {
            console.error('Error logged:', error);
        }
    }

    /**
     * エラーログを取得
     */
    public getErrorLog(): AppError[] {
        return [...this.errorLog];
    }

    /**
     * エラーログをクリア
     */
    public clearErrorLog(): void {
        this.errorLog = [];
    }

    /**
     * 特定のエラータイプのログを取得
     */
    public getErrorsByType(type: ErrorType): AppError[] {
        return this.errorLog.filter(error => error.code === type);
    }

    /**
     * 最近のエラーを取得
     */
    public getRecentErrors(count: number = 10): AppError[] {
        return this.errorLog.slice(-count);
    }
}

/**
 * エラーハンドラーのシングルトンインスタンス
 */
export const errorHandler = ErrorHandler.getInstance();

/**
 * エラーメッセージの多言語対応
 */
export const getErrorMessage = (error: AppError, locale: string = 'ja'): string => {
    const messages: Record<string, Record<string, string>> = {
        ja: {
            [ErrorType.NETWORK_ERROR]: 'ネットワークエラーが発生しました。インターネット接続を確認してください。',
            [ErrorType.TIMEOUT_ERROR]: 'リクエストがタイムアウトしました。しばらく時間をおいて再試行してください。',
            [ErrorType.AUTHENTICATION_ERROR]: '認証が必要です。ログインしてください。',
            [ErrorType.AUTHORIZATION_ERROR]: 'この操作を実行する権限がありません。',
            [ErrorType.VALIDATION_ERROR]: '入力データに問題があります。',
            [ErrorType.DATABASE_ERROR]: 'データベースエラーが発生しました。管理者にお問い合わせください。',
            [ErrorType.NOT_FOUND]: '指定されたリソースが見つかりません。',
            [ErrorType.DUPLICATE_ERROR]: '重複するデータが存在します。',
            [ErrorType.CONSTRAINT_ERROR]: '関連するデータが存在しないため、操作を実行できません。',
            [ErrorType.BUSINESS_LOGIC_ERROR]: 'ビジネスロジックエラーが発生しました。',
            [ErrorType.INSUFFICIENT_PERMISSIONS]: '権限が不足しています。',
            [ErrorType.RESOURCE_CONFLICT]: 'リソースの競合が発生しました。',
            [ErrorType.INTERNAL_ERROR]: '内部エラーが発生しました。管理者にお問い合わせください。',
            [ErrorType.EXTERNAL_SERVICE_ERROR]: '外部サービスエラーが発生しました。',
            [ErrorType.CONFIGURATION_ERROR]: '設定エラーが発生しました。',
            [ErrorType.USER_ERROR]: 'ユーザーエラーが発生しました。',
            [ErrorType.INVALID_OPERATION]: '無効な操作です。',
        },
        en: {
            [ErrorType.NETWORK_ERROR]: 'A network error occurred. Please check your internet connection.',
            [ErrorType.TIMEOUT_ERROR]: 'Request timed out. Please try again later.',
            [ErrorType.AUTHENTICATION_ERROR]: 'Authentication required. Please log in.',
            [ErrorType.AUTHORIZATION_ERROR]: 'You do not have permission to perform this operation.',
            [ErrorType.VALIDATION_ERROR]: 'There is a problem with the input data.',
            [ErrorType.DATABASE_ERROR]: 'A database error occurred. Please contact the administrator.',
            [ErrorType.NOT_FOUND]: 'The specified resource was not found.',
            [ErrorType.DUPLICATE_ERROR]: 'Duplicate data exists.',
            [ErrorType.CONSTRAINT_ERROR]: 'Cannot perform operation due to missing related data.',
            [ErrorType.BUSINESS_LOGIC_ERROR]: 'A business logic error occurred.',
            [ErrorType.INSUFFICIENT_PERMISSIONS]: 'Insufficient permissions.',
            [ErrorType.RESOURCE_CONFLICT]: 'Resource conflict occurred.',
            [ErrorType.INTERNAL_ERROR]: 'An internal error occurred. Please contact the administrator.',
            [ErrorType.EXTERNAL_SERVICE_ERROR]: 'External service error occurred.',
            [ErrorType.CONFIGURATION_ERROR]: 'Configuration error occurred.',
            [ErrorType.USER_ERROR]: 'User error occurred.',
            [ErrorType.INVALID_OPERATION]: 'Invalid operation.',
        },
    };

    return messages[locale]?.[error.code] || error.message;
};

/**
 * エラーの重要度に基づく処理
 */
export const handleErrorByLevel = (error: AppError, level: ErrorLevel): void => {
    switch (level) {
        case ErrorLevel.CRITICAL:
            // クリティカルエラーは即座にユーザーに通知
            console.error('CRITICAL ERROR:', error);
            // 必要に応じて外部ログサービスに送信
            break;

        case ErrorLevel.HIGH:
            // 高重要度エラーはログに記録し、ユーザーに通知
            console.error('HIGH PRIORITY ERROR:', error);
            break;

        case ErrorLevel.MEDIUM:
            // 中重要度エラーはログに記録
            console.warn('MEDIUM PRIORITY ERROR:', error);
            break;

        case ErrorLevel.LOW:
            // 低重要度エラーはログに記録のみ
            console.info('LOW PRIORITY ERROR:', error);
            break;
    }
};
